import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  toDateOrNull,
  formatDateToApi,
  extractSelectValue,
  formatSituacaoDepotToSelect,
  formatTipoTomadorToSelect,
  formatTipoPagamentoToSelect,
  formatCpfCnpj,
  sanitizeDigits,
  parseCurrencyToNumber,
  formatCurrencyBr,
} from '../formatters/formatters';

const statusOptions = [
  { value: 'coleta_no_porto', label: 'COLETA PORTO' },
  { value: 'baixado_aguardando_cheio_barra_velha', label: 'BX CHEIO BV' },
  { value: 'baixado_aguardando_cheio_itapoa', label: 'BX CHEIO ITA' },
  { value: 'baixado_aguardando_vazio_barra_velha', label: 'BX VAZIO BV' },
  { value: 'baixado_aguardando_vazio_itapoa', label: 'BX VAZIO ITA' },
  { value: 'finalizado', label: 'FINALIZADO' },
];

const formatStatusToSelect = (value) => {
  if (!value) return '';
  const found = statusOptions.find((opt) => opt.value === value);
  return found || { value, label: String(value).replace(/_/g, ' ').toUpperCase() };
};

/**
 * MAPPING API -> FORM (DETALHES)
 */
export const mapApiToForm = (dados = {}) => ({
  referenciaCliente: dados.ReferenciaCLiente || dados.ReferenciaCliente || '',
  tipoDepot: dados.TipoDepot || '',
  numeroContainer: dados.NumeroContainer || '',
  tipoContainer: dados.TipoContainer || '',
  numeroEmbarcador: dados.NumeroEmbarcador || '',
  localColeta: dados.LocalColeta || '',
  dataColeta: toDateOrNull(dados.DataColeta),
  statusPedido: dados.StatusPedido || '',
  dataEntrega: toDateOrNull(dados.DataEntrega),
  local: dados.Local || '',
  localDevolucao: dados.LocalDevolucao || '',
  dataDevolucao: toDateOrNull(dados.DataDevolucao),
  freeTime: toDateOrNull(dados.FreeTime),
  motDepot: dados.MotDepot || '',
  status: formatStatusToSelect(dados.Status || ''),
  situacaoDepot: formatSituacaoDepotToSelect(dados.SituacaoDepot),
  idMotoristaColeta: dados.IdMotoristaColeta ?? '',
  idMotoristaEntrega: dados.IdMotoristaEntrega ?? '',
  idMotoristaDevolucao: dados.IdMotoristaDevolucao ?? '',
  idVeiculoColeta: dados.IdVeiculoColeta ?? '',
  idCarretaColeta: dados.IdVeiculoColetaCarreta ?? '',
  idVeiculoEntrega: dados.IdVeiculoEntrega ?? '',
  IdVeiculoDevolucao: dados.IdVeiculoDevolucao ?? '',
  motoristaColetaNome: dados.IdMotoristaColeta
    ? { value: dados.IdMotoristaColeta, label: dados.MotoristaColeta?.Nome || '' }
    : '',
  motoristaColetaCpf: formatCpfCnpj(dados.MotoristaColeta?.Cpf || ''),
  motoristaEntregaNome: dados.IdMotoristaEntrega
    ? { value: dados.IdMotoristaEntrega, label: dados.MotoristaEntrega?.Nome || '' }
    : '',
  motoristaEntregaCpf: formatCpfCnpj(dados.MotoristaEntrega?.Cpf || ''),
  motoristaDevolucaoNome: dados.IdMotoristaDevolucao
    ? { value: dados.IdMotoristaDevolucao, label: dados.MotoristaDevolucao?.Nome || '' }
    : '',
  motoristaDevolucaoCpf: formatCpfCnpj(dados.MotoristaDevolucao?.Cpf || ''),
  veiculoColetaPlaca: dados.VeiculoColeta?.Placa || '',
  veiculoColetaFrota: dados.VeiculoColeta?.Frota || '',
  carretaColetaPlaca: dados.VeiculoColetaCarreta?.Placa || '',
  carretaColetaFrota: dados.VeiculoColetaCarreta?.Frota || '',
  veiculoEntregaPlaca: dados.VeiculoEntrega?.Placa || '',
  veiculoEntregaFrota: dados.VeiculoEntrega?.Frota || '',
  veiculoDevolucaoPlaca: dados.VeiculoDevolucao?.Placa || '',
  veiculoDevolucaoFrota: dados.VeiculoDevolucao?.Frota || '',
  anotacoesProgramacao: dados.AnotacoesProgramacao || '',
  anotacoesOperacionais: dados.AnotacoesOperacionais || '',
  alerta: dados.Alerta || '',
  statusPedidoEmissaoTMS: dados.StatusPedidoEmissaoTMS || '',
  transportadoraEmitenteCnpj: formatCpfCnpj(dados.TransportadoraEmitenteCnpj),
  remetenteCpfCnpj: formatCpfCnpj(dados.RemetenteCpfCnpj),
  destinatarioCpfCnpj: formatCpfCnpj(dados.DestinatarioCpfCnpj),
  tomadorCpfCnpj: formatCpfCnpj(
    dados.TomadorCpfCnpj ||
      dados?.Pedidos?.[0]?.TomadorCpfCnpj ||
      dados?.Pedido?.TomadorCpfCnpj ||
      dados?.pedido?.TomadorCpfCnpj,
  ),
  tipoTomador: formatTipoTomadorToSelect(
    dados.TipoTomador ??
      dados?.Pedidos?.[0]?.TipoTomador ??
      dados?.Pedido?.TipoTomador ??
      dados?.pedido?.TipoTomador,
  ),
  utilizarTipoTomadorInformado: !!dados.UtilizarTipoTomadorInformado,
  tipoPagamento: formatTipoPagamentoToSelect(dados.TipoPagamento),
  tipoOperacaoCodigo: dados.TipoOperacaoCodigo || '',
  tipoCargaEmbarcadorCodigo: dados.TipoCargaEmbarcadorCodigo || '',
  observacao: dados.Observacao || '',
  valorFrete: formatCurrencyBr(
    dados.ValorFrete ??
      dados?.Pedidos?.[0]?.ValorFrete ??
      dados?.Pedido?.ValorFrete ??
      dados?.pedido?.ValorFrete,
    { withSymbol: true },
  ),
});

/**
 * MAPPING FORM -> API (DETALHES)
 */
export const mapFormToApi = (values = {}) => ({
  ReferenciaCLiente: values.referenciaCliente || '',
  tipoDepot: values.tipoDepot || '',
  NumeroContainer: values.numeroContainer || '',
  TipoContainer: extractSelectValue(values.tipoContainer, { emptyAsNull: false }),
  NumeroEmbarcador: values.numeroEmbarcador || '',
  LocalColeta: values.localColeta || '',
  DataColeta: formatDateToApi(values.dataColeta),
  StatusPedido: extractSelectValue(values.statusPedido, { emptyAsNull: false }),
  DataEntrega: formatDateToApi(values.dataEntrega),
  Local: values.local || '',
  LocalDevolucao: values.localDevolucao || '',
  DataDevolucao: formatDateToApi(values.dataDevolucao),
  FreeTime: formatDateToApi(values.freeTime),
  MotDepot: values.motDepot || '',
  Status: extractSelectValue(values.status, { emptyAsNull: false }),
  SituacaoDepot: extractSelectValue(values.situacaoDepot),
  IdMotoristaColeta: extractSelectValue(values.motoristaColetaNome, { toNumber: true }),
  IdMotoristaEntrega: extractSelectValue(values.motoristaEntregaNome, { toNumber: true }),
  IdVeiculoColeta: values.idVeiculoColeta === '' ? null : values.idVeiculoColeta,
  IdVeiculoEntrega: values.idVeiculoEntrega === '' ? null : values.idVeiculoEntrega,
  IdVeiculoDevolucao: values.IdVeiculoDevolucao === '' ? null : values.IdVeiculoDevolucao,
  AnotacoesProgramacao: values.anotacoesProgramacao || '',
  AnotacoesOperacionais: values.anotacoesOperacionais || '',
  Alerta: values.alerta || '',
  StatusPedidoEmissaoTMS: values.statusPedidoEmissaoTMS || '',
  TransportadoraEmitenteCnpj: sanitizeDigits(values.transportadoraEmitenteCnpj),
  RemetenteCpfCnpj: sanitizeDigits(values.remetenteCpfCnpj),
  DestinatarioCpfCnpj: sanitizeDigits(values.destinatarioCpfCnpj),
  TomadorCpfCnpj: sanitizeDigits(values.tomadorCpfCnpj),
  TipoTomador: extractSelectValue(values.tipoTomador, { toNumber: true }),
  UtilizarTipoTomadorInformado: !!values.utilizarTipoTomadorInformado,
  TipoPagamento: extractSelectValue(values.tipoPagamento, { toNumber: true }),
  TipoOperacaoCodigo: values.tipoOperacaoCodigo || '',
  TipoCargaEmbarcadorCodigo: values.tipoCargaEmbarcadorCodigo || '',
  Observacao: values.observacao || '',
  ValorFrete: parseCurrencyToNumber(values.valorFrete),
});

/**
 * PEDIDO (API -> FORM)
 */
export const mapPedidoApiToForm = (dados = {}) => {
  const lista = dados?.Pedidos || dados?.pedidos || [];
  const pedido =
    (Array.isArray(lista) && lista.length ? lista[0] : null) ||
    dados?.Pedido ||
    dados?.pedido ||
    {};

  const fonte = pedido && Object.keys(pedido || {}).length > 0 ? pedido : dados || {};

  return {
    dataColeta: toDateOrNull(fonte.dataColeta || fonte.DataColeta),
    statusPedidoEmissaoTMS: fonte.statusPedidoEmissaoTMS || fonte.StatusPedidoEmissaoTMS || '',
    transportadoraEmitenteCnpj: formatCpfCnpj(
      fonte.transportadoraEmitenteCnpj || fonte.TransportadoraEmitenteCnpj || '',
    ),
    remetenteCpfCnpj: formatCpfCnpj(fonte.remetenteCpfCnpj || fonte.RemetenteCpfCnpj || ''),
    destinatarioCpfCnpj: formatCpfCnpj(
      fonte.destinatarioCpfCnpj || fonte.DestinatarioCpfCnpj || '',
    ),
    tomadorCpfCnpj: formatCpfCnpj(fonte.tomadorCpfCnpj || fonte.TomadorCpfCnpj),
    tipoTomador: formatTipoTomadorToSelect(fonte.tipoTomador ?? fonte.TipoTomador),
    utilizarTipoTomadorInformado:
      fonte.utilizarTipoTomadorInformado ?? fonte.UtilizarTipoTomadorInformado ?? false,
    tipoPagamento: formatTipoPagamentoToSelect(fonte.tipoPagamento ?? fonte.TipoPagamento),
    tipoOperacaoCodigo: fonte.tipoOperacaoCodigo || fonte.TipoOperacaoCodigo || '',
    tipoCargaEmbarcadorCodigo:
      fonte.tipoCargaEmbarcadorCodigo || fonte.TipoCargaEmbarcadorCodigo || '',
    observacao: fonte.observacao || fonte.Observacao || '',
    valorFrete: formatCurrencyBr(fonte.valorFrete ?? fonte.ValorFrete, { withSymbol: true }),
  };
};

export const useDetalhesTab = (_depotId, depotData) => {
  const [anexos, setAnexos] = useState([]);
  const [anexosLoading, setAnexosLoading] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const initialValues = useMemo(() => mapApiToForm(depotData || {}), [depotData]);

  const loadAnexos = useCallback(() => {
    if (!isMountedRef.current) return;
    const lista = depotData?.Anexos ?? depotData?.anexos ?? [];
    setAnexos(Array.isArray(lista) ? lista : []);
  }, [depotData]);

  useEffect(() => {
    if (!isMountedRef.current) return;
    setAnexosLoading(true);
    loadAnexos();
    setAnexosLoading(false);
  }, [loadAnexos]);

  return {
    initialValues,
    anexos,
    anexosLoading,
    loadAnexos,
    mapApiToForm,
    mapFormToApi,
    mapPedidoApiToForm,
  };
};
